import { DRIVING_SURFACE_TYPES, type DrivingSurfaceType } from './drivingSurface';

/**
 * 노면의 짧은 파장 요철.
 *
 * drivingTerrain의 높이장은 언덕 같은 **긴** 지형이고, 이것은 그 위에 얹히는 수 센티미터
 * 단위의 거칠기입니다. 서스펜션이 실제로 움직이려면 이 성분이 필요합니다.
 *
 * Math.random을 쓰지 않는 이유: 챌린지 기록과 고정 스텝 재현성이 깨집니다. 월드 좌표를
 * 입력으로 하는 결정론적 함수라 같은 자리를 지나면 언제나 같은 요철이 나옵니다.
 *
 * 머티리얼 roughness(반사)와는 다른 값입니다. 이름을 나눠 두지 않으면 시각 파라미터와
 * 물리 파라미터가 섞입니다.
 */

interface SurfaceRoughness {
  /** 요철 진폭 (m). */
  amplitude: number;
  /** 대표 파장 (m). 짧을수록 고주파 진동이 됩니다. */
  wavelength: number;
}

const SURFACE_ROUGHNESS: Record<DrivingSurfaceType, SurfaceRoughness> = {
  asphalt: { amplitude: 0.004, wavelength: 7.5 },
  grass: { amplitude: 0.032, wavelength: 3.1 },
  gravel: { amplitude: 0.045, wavelength: 1.7 },
  puddle: { amplitude: 0.01, wavelength: 4.5 },
};

/** 결정론적 의사 난수. 같은 (x, z)에서 항상 같은 값을 돌려줍니다. */
function hashNoise(x: number, z: number): number {
  const value = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
  return value - Math.floor(value);
}

/** 격자 보간으로 부드럽게 이어지는 값. 정점 사이가 튀지 않습니다. */
function smoothNoise(x: number, z: number): number {
  const cellX = Math.floor(x);
  const cellZ = Math.floor(z);
  const fracX = x - cellX;
  const fracZ = z - cellZ;
  const easeX = fracX * fracX * (3 - 2 * fracX);
  const easeZ = fracZ * fracZ * (3 - 2 * fracZ);

  const c00 = hashNoise(cellX, cellZ);
  const c10 = hashNoise(cellX + 1, cellZ);
  const c01 = hashNoise(cellX, cellZ + 1);
  const c11 = hashNoise(cellX + 1, cellZ + 1);

  const top = c00 + (c10 - c00) * easeX;
  const bottom = c01 + (c11 - c01) * easeX;
  // −1~1로 펴서 노면이 평균적으로 올라가거나 내려가지 않게 합니다.
  return (top + (bottom - top) * easeZ) * 2 - 1;
}

/**
 * 해당 지점의 요철 높이(m)를 돌려줍니다.
 *
 * 두 주파수를 겹칩니다. 낮은 쪽은 노면의 완만한 굴곡, 높은 쪽은 자갈처럼 잔 진동입니다.
 */
export function sampleRoadProfile(
  x: number,
  z: number,
  surfaceType: DrivingSurfaceType,
  detailScale = 1
): number {
  const roughness = SURFACE_ROUGHNESS[surfaceType] ?? SURFACE_ROUGHNESS.asphalt;
  if (roughness.amplitude <= 0) {
    return 0;
  }
  const base = smoothNoise(x / roughness.wavelength, z / roughness.wavelength);
  const detail = smoothNoise(
    (x + 37.1) / (roughness.wavelength * 0.34),
    (z - 19.7) / (roughness.wavelength * 0.34)
  );
  /*
   * detailScale은 표본 간격 대비 짧은 옥타브의 신뢰도입니다. 호출자가 속도에서 계산해
   * 넘기며, 1이면 그대로 쓰고 0이면 완만한 성분만 남깁니다. 이렇게 하지 않으면 고속에서
   * 짧은 파장이 표본화 한계를 넘어 노면이 잡음처럼 떱니다.
   */
  const detailWeight = 0.28 * Math.min(1, Math.max(0, detailScale));
  return (base * 0.72 + detail * detailWeight) * roughness.amplitude;
}

/** 표본 간격(m)에서 짧은 옥타브를 얼마나 신뢰할 수 있는지 돌려줍니다. */
export function resolveDetailScale(
  sampleSpacing: number,
  surfaceType: DrivingSurfaceType
): number {
  const roughness = SURFACE_ROUGHNESS[surfaceType] ?? SURFACE_ROUGHNESS.asphalt;
  const detailWavelength = roughness.wavelength * 0.34;
  if (sampleSpacing <= 0) {
    return 1;
  }
  // 파장 하나에 표본이 4개 이상이면 그대로, 2개 이하면 0으로 줄입니다.
  const samplesPerWavelength = detailWavelength / sampleSpacing;
  return Math.min(1, Math.max(0, (samplesPerWavelength - 2) / 2));
}

export function getSurfaceRoughnessAmplitude(surfaceType: DrivingSurfaceType): number {
  return (SURFACE_ROUGHNESS[surfaceType] ?? SURFACE_ROUGHNESS.asphalt).amplitude;
}

export { DRIVING_SURFACE_TYPES };
